import type { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faPlus } from '@fortawesome/pro-light-svg-icons';

import type { FieldCollection } from '@feathr/blackbox';
import { FieldDataType } from '@feathr/blackbox';
import { fieldDataTypeIcon } from '@feathr/extender/styles/fieldDataType';

/**
 * The option shape the field-related select option components read from. Every field is optional
 * because the options come from several sources — custom fields, default field definitions, and
 * synthetic "create new field" entries — each of which populates a different subset.
 */
export interface IFieldOptionData {
  collection?: FieldCollection;
  data_type?: FieldDataType;
  id?: string;
  key?: string;
  label?: string;
  name?: string;
  u_key?: string;
  value?: string | number | boolean | Date;
}

/**
 * Infers a field's data type from a sample value, for options that carry a value but no explicit
 * `data_type`.
 */
export function valueToFieldDataType(
  value: string | number | boolean | Date | undefined,
): FieldDataType {
  if (typeof value === 'string') {
    return FieldDataType.str;
  }
  if (typeof value === 'number') {
    return FieldDataType.int;
  }
  if (typeof value === 'boolean') {
    return FieldDataType.bool;
  }
  if (value instanceof Date) {
    return FieldDataType.date;
  }
  return FieldDataType.str;
}

/** Falls back to the value's inferred type when a field carries no explicit `data_type`. */
export function getFieldDataTypeIcon(field: IFieldOptionData): IconDefinition {
  return fieldDataTypeIcon(field.data_type ? field.data_type : valueToFieldDataType(field.value));
}

/** The same, except the synthetic "create this field" option gets a plus. Menu rows only. */
export function getFieldIcon(field: IFieldOptionData): IconDefinition {
  if (field.id === 'create') {
    return faPlus;
  }
  return getFieldDataTypeIcon(field);
}

/** Field selects label an option by its user-facing key; blank rather than throwing when absent. */
export function getFieldOptionLabel(field: IFieldOptionData): string {
  return field.u_key ?? '';
}
